import { useEffect, useState } from 'react';
import { useWebApp } from './hooks/useWebApp';
import { api, setApiContext } from './api';
import { SurveyPage } from './pages/SurveyPage';
import { SuccessPage } from './pages/SuccessPage';
import { AdminPage } from './pages/AdminPage';
import './App.css';

export default function App() {
  const { ready, initData, platform, startParam, eventCode, isAdminMode, isDev } = useWebApp();
  const [lotteryNumber, setLotteryNumber] = useState<number | null>(null);
  const [eventName, setEventName] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [resolvedEventCode, setResolvedEventCode] = useState<string | null>(null);
  const [resolvedAdminMode, setResolvedAdminMode] = useState(false);
  const [bootError, setBootError] = useState('');
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    if (!ready) return;

    if (!initData.trim()) {
      setBootError(
        platform === 'max'
          ? 'Не удалось получить данные авторизации MAX. Закройте mini app и откройте снова из бота.'
          : 'Требуется авторизация мессенджера.'
      );
      setBooting(false);
      return;
    }

    setApiContext({ initData, platform, startParam });
    setBooting(true);

    api
      .getMe()
      .then((me) => {
        setIsAdmin(me.isAdmin);
        const code = me.eventCode ?? eventCode ?? null;
        setResolvedEventCode(code);
        setResolvedAdminMode(!code && (me.isAdminMode || isAdminMode));
        setBootError('');
      })
      .catch((e) => {
        const msg = e instanceof Error ? e.message : 'Ошибка авторизации';
        if (isDev && import.meta.env.DEV) {
          console.warn('[dev] API:', msg);
          setIsAdmin(true);
          setResolvedEventCode(eventCode);
          setResolvedAdminMode(isAdminMode);
          setBootError('');
        } else {
          setBootError(msg);
        }
      })
      .finally(() => setBooting(false));
  }, [ready, initData, platform, startParam, eventCode, isAdminMode, isDev]);

  const activeEventCode = resolvedEventCode ?? eventCode;
  const showAdmin = resolvedAdminMode && !activeEventCode;

  if (!ready || booting) {
    return (
      <div className="app">
        <div className="loading">Запуск…</div>
      </div>
    );
  }

  if (bootError) {
    return (
      <div className="app">
        <div className="error">{bootError}</div>
      </div>
    );
  }

  if (showAdmin) {
    return (
      <div className="app">
        <AdminPage isAdmin={isAdmin} demoPlatform={platform} />
      </div>
    );
  }

  if (lotteryNumber !== null) {
    return (
      <div className="app">
        <SuccessPage lotteryNumber={lotteryNumber} eventName={eventName} />
      </div>
    );
  }

  if (!activeEventCode) {
    return (
      <div className="app">
        <div className="header">
          <div className="logo">Клиника Авиценна</div>
          <h1 className="title">Анкета мероприятия</h1>
        </div>
        <div className="card">
          <p style={{ margin: 0 }}>
            Отсканируйте QR-код на мероприятии, чтобы начать анкетирование.
          </p>
          {(isAdmin || isDev) && (
            <button
              type="button"
              className="btn btn-secondary"
              style={{ marginTop: 16 }}
              onClick={() => {
                window.location.search = '?mode=admin';
              }}
            >
              Админ-панель
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <SurveyPage
        eventCode={activeEventCode}
        onSuccess={async (num) => {
          setLotteryNumber(num);
          try {
            const evt = await api.getEventByCode(activeEventCode);
            setEventName(evt.name);
          } catch {
            // ignore
          }
        }}
      />
    </div>
  );
}
